import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { SquarePen } from 'lucide-react'
import { BUI_EASE, Button, PromptBar } from '@loop3/ui'
import { CHAT_LIMITS } from '@loop3/universe'
import { LupeElsewhere } from '@/components/lupe-elsewhere'
import { LupeThread } from '@/components/lupe-thread'
import { useLupeThread } from '@/hooks/useLupeThread'

const SUGGESTIONS = [
  'What did we decide this week?',
  'What are my open action items?',
  'Who knows MCP gateways?',
  'Have we built a CRM integration?',
  'Who do we know at Greylock?',
  'Give me an overview of our clients',
]

/** Lupe-first home: a hero with suggestions until the first question, then the thread over a docked prompt. */
export function LupePage() {
  const { turns, pending, send, retry, clear } = useLupeThread()
  const [draft, setDraft] = useState('')
  const [params, setParams] = useSearchParams()
  const askHandled = useRef(false)
  const promptRef = useRef<HTMLDivElement>(null)
  const empty = turns.length === 0 && !pending

  const focusPrompt = () =>
    promptRef.current?.querySelector('textarea')?.focus()

  const submit = (text: string) => {
    if (pending || !text.trim()) return
    send(text)
    setDraft('')
    focusPrompt()
  }

  // The prompt is the page's purpose, so it takes focus on arrival.
  useEffect(() => focusPrompt(), [])

  // `?ask=` from elsewhere in the app sends once; the ref absorbs StrictMode's second effect run.
  useEffect(() => {
    const question = params.get('ask')
    if (!question) {
      askHandled.current = false
      return
    }
    if (askHandled.current) return
    askHandled.current = true
    // While Lupe is busy the question waits in the prompt instead of being dropped.
    if (pending) setDraft(question)
    else send(question)
    setParams({}, { replace: true })
  }, [params, pending, send, setParams])

  return (
    <div className="mx-auto flex min-h-[calc(100svh-7.5rem)] w-full max-w-3xl flex-col">
      {empty ? (
        <div className="flex flex-1 flex-col items-center justify-end pb-6 text-center">
          <img
            src="/lupe.png"
            alt=""
            width={96}
            height={96}
            className="size-24"
          />
          <h1 className="mt-5 text-balance font-display text-display-sm leading-[1.05] font-light">
            Hi, I'm Lupe
          </h1>
          <p className="mt-3 text-balance text-muted-foreground">
            I can look up meetings, decisions, clients, people, and past work.
          </p>
        </div>
      ) : (
        <div className="flex-1 pb-6">
          <div className="mb-6 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                clear()
                focusPrompt()
              }}
              disabled={pending}
            >
              <SquarePen aria-hidden />
              New conversation
            </Button>
          </div>
          <LupeThread
            turns={turns}
            pending={pending}
            onRetry={() => {
              retry()
              focusPrompt()
            }}
          />
        </div>
      )}

      <div
        ref={promptRef}
        className={
          empty
            ? ''
            : 'sticky bottom-0 -mx-6 bg-gradient-to-t from-background from-60% to-transparent px-6 pt-8 pb-4'
        }
      >
        <PromptBar
          value={draft}
          onChange={setDraft}
          onSubmit={() => submit(draft)}
          busy={pending}
          maxLength={CHAT_LIMITS.message}
          placeholder="Ask Lupe a question"
        />
      </div>

      {empty && (
        <ul
          aria-label="Suggestions"
          className="flex flex-1 flex-wrap content-start justify-center gap-2 pt-5"
        >
          {SUGGESTIONS.map((s, i) => (
            <li
              key={s}
              className="bui-motion"
              style={{
                animation: `bui-fade-up 360ms ${BUI_EASE} ${120 + i * 50}ms both`,
              }}
            >
              <button
                type="button"
                onClick={() => submit(s)}
                className="rounded-full border border-border px-3.5 py-1.5 text-sm transition-colors hover:border-[var(--l3-violet)] hover:bg-[var(--l3-violet-soft)] hover:text-[var(--l3-violet)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--l3-violet)]"
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}

      {empty && <LupeElsewhere />}
    </div>
  )
}
